/**
 * 编辑器 UI 状态 —— 模块级 reactive 单例（不引 Pinia：状态就这几项）。
 * 这里只放「界面状态」，文档本体永远在真实 DOM 里，不进 store（PLAN §8.2 第 5 条）。
 */
import { reactive } from 'vue';
import type { ViewState } from '@/core/geom/viewport';

export type Tool = 'select' | 'hand' | 'rect' | 'ellipse' | 'line' | 'polyline' | 'text';

/** 需要"画"的工具（画的时候要让路：不做选择、不做变换） */
export const DRAW_TOOLS: readonly Tool[] = ['rect', 'ellipse', 'line', 'polyline', 'text'];
export const isDrawTool = (tool: Tool): boolean => DRAW_TOOLS.includes(tool);
export type InspectorTab = 'props' | 'arrange' | 'canvas' | 'palette';

/* ---------------------------------------------------------------- 分栏宽度
 * 左右两侧可拖拽调宽；**每个页签各记一份右栏宽度**（色板是两栏工作台，天然更宽）。
 * 宽度属于"布局偏好"，存 localStorage，打开新文档也不重置。
 */
export const LIST_MIN = 180;
export const LIST_MAX = 520;
export const INSP_MIN = 248;
export const INSP_MAX = 980;
/** 右栏默认宽度：色板页签更宽（对齐参考产品的宽色板） */
const INSP_DEFAULT = { palette: 560, other: 308 } as const;
const LAYOUT_KEY = 'svg-edit-lite:layout';

export const clampPanel = (width: number, min: number, max: number): number =>
  Number.isFinite(width) ? Math.min(max, Math.max(min, Math.round(width))) : min;

export type PanelSide = 'list' | 'right';

/** 各页签的右栏宽度记忆（other 覆盖 属性/排列/画布） */
const inspMemory: { palette: number; other: number } = { ...INSP_DEFAULT };
const slotOf = (tab: InspectorTab): 'palette' | 'other' => (tab === 'palette' ? 'palette' : 'other');

export interface EditorUiState extends ViewState {
  tool: Tool;
  inspectorTab: InspectorTab;
  search: string;
  /** 分组折叠态：groupKey → 是否折叠 */
  collapsed: Record<string, boolean>;
  status: string;
  busy: boolean;
  /** 双击进入的分组上下文（null = 顶层）；用于「进组/出组」选择语义 */
  groupContext: Element | null;
  snapEnabled: boolean;
  gridSize: number;
  /** 画板背景色（**仅编辑器视图**，不写文档）；null = 用文档里的 style 或默认白 */
  canvasBg: string | null;
  /** 源码面板是否展开 */
  codeOpen: boolean;
  /** 导出面板（状态栏「导出 ▾」）是否展开 */
  exportOpen: boolean;
  /** 正有文件被拖进窗口（空画板用来点亮拖拽落区） */
  dragOver: boolean;
  /** 空画板引导卡被手动关掉；画板再次变空时重新武装（见 CanvasStage 的 watch） */
  blankDismissed: boolean;
  /**
   * **位置锁**：开着时文档里**原有的图元**位置/尺寸/旋转一律改不动（拖动、手柄、方向键、
   * 坐标输入、对齐分布全被拦），但仍可点击选中、改颜色/文字。
   * 本次会话新建的元素（`ext-*` 那批）不受限 —— 否则新画的批注框没法摆位。
   * 属视图态（不写文档、不进工程文件），**每次装载文档重置为开**。
   */
  geometryLocked: boolean;
  /** 左栏（图层）宽度 px */
  listW: number;
  /** 右栏（属性/色板）宽度 px */
  inspW: number;
}

/** 空闲态文案：选中文案与其它提示都往这里回落；空画板时状态栏另说（见 StatusBar） */
export const IDLE_STATUS = '未选中元素 · 点击左侧列表或画布图元开始编辑';

/** 位置锁拦下一次改动时的提示（状态栏是提示的唯一出口，见 StatusBar 的 data-field="status"） */
export const POSITION_LOCKED_STATUS = '位置已锁定 · 右栏「位置与尺寸」里可解锁';

export const ui = reactive<EditorUiState>({
  zoom: 1,
  panX: 0,
  panY: 0,
  tool: 'select',
  inspectorTab: 'props',
  search: '',
  collapsed: {},
  status: IDLE_STATUS,
  busy: false,
  groupContext: null,
  snapEnabled: false,
  gridSize: 8,
  canvasBg: null,
  codeOpen: false,
  /** 导出面板（状态栏「导出 ▾」） */
  exportOpen: false,
  dragOver: false,
  blankDismissed: false,
  geometryLocked: true,
  listW: 240,
  inspW: INSP_DEFAULT.other,
});

/** 切页签：先把当前宽度记进旧槽位，再套用新页签的宽度（用户拖过就记住，不再被自动改） */
export function setInspectorTab(tab: InspectorTab): void {
  if (tab !== ui.inspectorTab) {
    inspMemory[slotOf(ui.inspectorTab)] = ui.inspW;
    ui.inspectorTab = tab;
    ui.inspW = clampPanel(inspMemory[slotOf(tab)], INSP_MIN, INSP_MAX);
    saveLayout();
  }
}

export function setPanelWidth(side: PanelSide, width: number): void {
  if (side === 'list') {
    ui.listW = clampPanel(width, LIST_MIN, LIST_MAX);
    return;
  }
  ui.inspW = clampPanel(width, INSP_MIN, INSP_MAX);
  inspMemory[slotOf(ui.inspectorTab)] = ui.inspW;
}

export function resetPanelWidth(side: PanelSide): void {
  if (side === 'list') ui.listW = 240;
  else {
    ui.inspW = INSP_DEFAULT[slotOf(ui.inspectorTab)];
    inspMemory[slotOf(ui.inspectorTab)] = ui.inspW;
  }
}

export function saveLayout(): void {
  try {
    localStorage.setItem(
      LAYOUT_KEY,
      JSON.stringify({ listW: ui.listW, inspW: ui.inspW, memory: inspMemory }),
    );
  } catch {
    /* 隐私模式等：忽略 */
  }
}

export function loadLayout(): void {
  try {
    const raw = localStorage.getItem(LAYOUT_KEY);
    if (!raw) return;
    const data = JSON.parse(raw) as {
      listW?: number;
      inspW?: number;
      memory?: { palette?: number; other?: number };
    };
    if (typeof data.memory?.palette === 'number') inspMemory.palette = data.memory.palette;
    if (typeof data.memory?.other === 'number') inspMemory.other = data.memory.other;
    if (typeof data.listW === 'number') ui.listW = clampPanel(data.listW, LIST_MIN, LIST_MAX);
    if (typeof data.inspW === 'number') {
      ui.inspW = clampPanel(data.inspW, INSP_MIN, INSP_MAX);
      inspMemory[slotOf(ui.inspectorTab)] = ui.inspW;
    }
  } catch {
    /* 坏数据忽略 */
  }
}

export const toggleCollapsed = (key: string): void => {
  ui.collapsed[key] = !ui.collapsed[key];
};

export const isCollapsed = (key: string): boolean => ui.collapsed[key] === true;

/** 重置全部 UI 状态（打开新文件时用） */
export function resetUi(): void {
  ui.zoom = 1;
  ui.panX = 0;
  ui.panY = 0;
  ui.tool = 'select';
  setInspectorTab('props');
  ui.search = '';
  ui.collapsed = {};
  ui.status = IDLE_STATUS;
  ui.groupContext = null;
  ui.snapEnabled = false;
  ui.canvasBg = null;
  ui.codeOpen = false;
  ui.dragOver = false;
  ui.blankDismissed = false;
  ui.geometryLocked = true;
}

/** 当前网格（关闭时返回 0，交给 op 跳过吸附） */
export const gridSize = (): number => (ui.snapEnabled ? ui.gridSize : 0);
